/**
 * Pedidos (Productos del Pedido):
 * - Recoge en bodega + Xerox (95) / ML fullfillment (93) / ML Distrito Cafetero (94): solo `stock_xerox` (OFIMA) o bodega XEROX en Flexy.
 * - Cualquier otro caso: solo FULFILLMENT (`stock_ecomerce` OFIMA o bodega FULFILLMENT Flexy).
 *   No se suman GSM, temporal ni `stock_disponible` global si hay bodegas Flexy.
 *   Por almacén Flexy: `stock_disponible` (neto); fallback `stock_flexy` (bruto).
 *
 * Sin opciones de pedido (catálogo legacy): `stock` + GSM + Distrito.
 * Si Flexy no tiene array de bodegas: fallback a `stock_disponible`.
 */

import { bodegaFacturacion, flexyAlmacenes } from '../assets/data/arrays'

export type OrderBodegaStockMode = 'xerox_only' | 'combined' | 'default' | 'ecommerce_only'

export type ProductStockWarehouseField =
  | 'stock_xerox'
  | 'stock_gsm'
  | 'stock_distrito'
  | 'stock_bodtemporal'

/** Ecommerce (typo en API: stock_ecomerce) */
export type ProductStockEcommerceField = 'stock_ecomerce' | 'stock_ecommerce'

/** Bodegas sumadas en pedidos (modo combined), sin Xerox ni Distrito. */
const ORDER_COMBINED_WAREHOUSE_FIELDS = ['stock_gsm', 'stock_bodtemporal'] as const

/** Legacy: listado catálogo sin modo pedido. */
const LEGACY_SUM_WAREHOUSE_FIELDS = ['stock_gsm', 'stock_distrito'] as const

/** Campos por bodega OFIMA; `stock` genérico (Flexy) no entra aquí. */
const PER_WAREHOUSE_STOCK_FIELD_KEYS = [
  'stock_xerox',
  'stock_gsm',
  'stock_distrito',
  'stock_bodtemporal',
  'stock_ecomerce',
  'stock_ecommerce'
] as const

function isStockFieldPresent(raw: unknown): boolean {
  return raw !== undefined && raw !== null && String(raw).trim() !== ''
}

export function hasPerWarehouseStockFields(product: Record<string, unknown>): boolean {
  return PER_WAREHOUSE_STOCK_FIELD_KEYS.some((key) => isStockFieldPresent(product[key]))
}

/** @deprecated usar hasPerWarehouseStockFields */
export function hasWarehouseStockFields(product: Record<string, unknown>): boolean {
  return hasPerWarehouseStockFields(product)
}

export function getStockDisponibleSaldo(product: Record<string, unknown>): number {
  return parseProductStockField(product.stock_disponible)
}

export type FlexyWarehouseStockEntry = {
  idalmacen?: number
  codigo?: string
  nombre?: string
  /** Neto por bodega (bruto − comprometido). Preferido en pedidos. */
  stock_disponible?: unknown
  stock_flexy?: unknown
  synced_at?: string
}

/** Saldo por fila de flexy_almacenes: stock_disponible; fallback stock_flexy. */
export function parseFlexyWarehouseEntryStock(
  entry: FlexyWarehouseStockEntry
): number | null {
  if (isStockFieldPresent(entry.stock_disponible)) {
    return parseProductStockField(entry.stock_disponible)
  }
  if (isStockFieldPresent(entry.stock_flexy)) {
    return parseProductStockField(entry.stock_flexy)
  }
  return null
}

const FLEXY_WAREHOUSE_ARRAY_KEYS = [
  'flexy_almacenes',
  'warehouses',
  'almacenes',
  'bodegas',
  'warehouse_stocks'
] as const

const FLEXY_XEROX_WAREHOUSE_CODES = new Set(['XER-MDM', 'XEROX'])
const FLEXY_FULFILLMENT_WAREHOUSE_CODES = new Set(['FUL-MDM', 'FULFILLMENT'])

function flexyIdByCodigo(codigo: string): number {
  const warehouse = flexyAlmacenes.find((item) => item.codigo === codigo)
  return Number(warehouse?.idalmacen ?? 0)
}

const FLEXY_XEROX_IDALMACEN = flexyIdByCodigo('XER-MDM')
const FLEXY_FULFILLMENT_IDALMACEN = flexyIdByCodigo('FUL-MDM')

function getFlexyWarehouseEntries(product: Record<string, unknown>): FlexyWarehouseStockEntry[] {
  for (const key of FLEXY_WAREHOUSE_ARRAY_KEYS) {
    const raw = product[key]
    if (Array.isArray(raw) && raw.length > 0) {
      return raw as FlexyWarehouseStockEntry[]
    }
  }
  return []
}

function isFlexyXeroxWarehouse(entry: FlexyWarehouseStockEntry): boolean {
  if (Number(entry.idalmacen) === FLEXY_XEROX_IDALMACEN) return true
  const codigo = String(entry.codigo ?? '').trim().toUpperCase()
  if (FLEXY_XEROX_WAREHOUSE_CODES.has(codigo)) return true
  const nombre = String(entry.nombre ?? '').trim().toUpperCase()
  return nombre === 'XEROX'
}

function isFlexyFulfillmentWarehouse(entry: FlexyWarehouseStockEntry): boolean {
  if (Number(entry.idalmacen) === FLEXY_FULFILLMENT_IDALMACEN) return true
  const codigo = String(entry.codigo ?? '').trim().toUpperCase()
  const nombre = String(entry.nombre ?? '').trim().toUpperCase()
  if (FLEXY_FULFILLMENT_WAREHOUSE_CODES.has(codigo)) return true
  return nombre === 'FULFILLMENT' || nombre.includes('FULFILLMENT')
}

/** Stock Flexy por bodega XEROX o FULFILLMENT en array `flexy_almacenes` / `warehouses`. */
export function getFlexyWarehouseStock(
  product: Record<string, unknown>,
  kind: 'xerox' | 'fulfillment'
): number | null {
  const entries = getFlexyWarehouseEntries(product)
  if (entries.length === 0) return null
  const matchFn = kind === 'xerox' ? isFlexyXeroxWarehouse : isFlexyFulfillmentWarehouse
  const entry = entries.find(matchFn)
  if (!entry) return null
  return parseFlexyWarehouseEntryStock(entry)
}

/** Stock bruto en `flexy_almacenes` por idalmacen Flexy. */
export function getFlexyAlmacenStock(
  product: Record<string, unknown>,
  idalmacen: number
): number | null {
  const entries = getFlexyWarehouseEntries(product)
  const entry = entries.find((row) => Number(row.idalmacen) === idalmacen)
  if (!entry) return null
  return parseFlexyWarehouseEntryStock(entry)
}

export function isFlexyStockFieldPresent(raw: unknown): boolean {
  return isStockFieldPresent(raw)
}

/** Campos planos de GET /flexy/all (disponible = bruto − comprometido). */
export const FLEXY_STOCK_FIELDS = {
  fulfillmentBruto: 'stock_fulfillment',
  fulfillmentDisponible: 'stock_fulfillment_disponible',
  xeroxBruto: 'stock_xerox_bruto',
  xeroxDisponible: 'stock_xerox',
  stock: 'stock',
  disponibleVenta: 'stock_disponible_venta'
} as const

function readFlexyFlatStockField(
  product: Record<string, unknown>,
  field: string
): number | null {
  if (!isStockFieldPresent(product[field])) return null
  return parseProductStockField(product[field])
}

/** Indica respuesta plana GET /flexy/all (no confundir `stock` legacy con fulfillment). */
export function hasFlexyFlatStockFields(product: Record<string, unknown>): boolean {
  return (
    isStockFieldPresent(product[FLEXY_STOCK_FIELDS.fulfillmentBruto]) ||
    isStockFieldPresent(product[FLEXY_STOCK_FIELDS.fulfillmentDisponible]) ||
    isStockFieldPresent(product[FLEXY_STOCK_FIELDS.xeroxBruto]) ||
    isStockFieldPresent(product[FLEXY_STOCK_FIELDS.disponibleVenta])
  )
}

/** Fulfillment neto (FUL-MDM): no usar `stock` recalculado en productDetails. */
export function getFlexyFulfillmentDisponibleStock(
  product: Record<string, unknown>
): number | null {
  const disponible = readFlexyFlatStockField(
    product,
    FLEXY_STOCK_FIELDS.fulfillmentDisponible
  )
  if (disponible != null) return disponible

  const fromAlmacen = getFlexyWarehouseStock(product, 'fulfillment')
  if (fromAlmacen != null) return fromAlmacen

  if (!hasFlexyFlatStockFields(product)) return null

  const venta = readFlexyFlatStockField(product, FLEXY_STOCK_FIELDS.disponibleVenta)
  if (venta != null) return venta

  return readFlexyFlatStockField(product, 'stock_disponible')
}

/** Xerox neto (XER-MDM): stock_xerox plano, luego flexy_almacenes. */
export function getFlexyXeroxDisponibleStock(
  product: Record<string, unknown>
): number | null {
  const fromFlat = readFlexyFlatStockField(product, FLEXY_STOCK_FIELDS.xeroxDisponible)
  if (fromFlat != null) return fromFlat
  return getFlexyWarehouseStock(product, 'xerox')
}

function getXeroxDisplayStock(product: Record<string, unknown>): number {
  const fromFlat = getFlexyXeroxDisponibleStock(product)
  if (fromFlat != null) return fromFlat
  const flexyXerox = getFlexyWarehouseStock(product, 'xerox')
  if (flexyXerox != null) return flexyXerox
  return parseProductStockField(product.stock_xerox)
}

function getFulfillmentDisplayStock(product: Record<string, unknown>): number {
  const fromFlat = getFlexyFulfillmentDisponibleStock(product)
  if (fromFlat != null) return fromFlat
  return getProductEcommerceStock(product)
}

export function parseProductStockField(raw: unknown): number {
  if (raw === undefined || raw === null) return 0
  const n = Number(raw)
  return Number.isFinite(n) ? n : 0
}

/** Saldo base del producto (sin bodegas adicionales por campo). */
export function getProductBaseSaldo(product: Record<string, unknown>): number {
  return parseProductStockField(product.stock ?? product.stock_quantity)
}

export function getProductEcommerceStock(product: Record<string, unknown>): number {
  return parseProductStockField(product.stock_ecomerce ?? product.stock_ecommerce)
}

export type ComputeProductDisplayStockOptions = {
  /** Solo catálogo legacy: suma ecommerce además de GSM + Distrito */
  includeEcommerce?: boolean
  /** Modo según bodega de recogida en pedidos */
  bodegaStockMode?: OrderBodegaStockMode
  /** @deprecated usar bodegaStockMode */
  xeroxBodegaOnly?: boolean
}

function resolveBodegaStockMode(
  options?: ComputeProductDisplayStockOptions
): 'xerox_only' | 'ecommerce_only' | 'combined' | 'legacy_default' {
  if (options?.bodegaStockMode === 'xerox_only' || options?.xeroxBodegaOnly) {
    return 'xerox_only'
  }
  if (options?.bodegaStockMode === 'ecommerce_only') {
    return 'ecommerce_only'
  }
  if (
    options?.bodegaStockMode === 'combined' ||
    options?.bodegaStockMode === 'default'
  ) {
    return 'combined'
  }
  if (options?.bodegaStockMode) {
    return 'combined'
  }
  return 'legacy_default'
}

/** @deprecated Pedidos ya no suman bodegas; usar getFulfillmentDisplayStock vía modo combined. */
export function computeOrderCombinedStock(product: Record<string, unknown>): number {
  let total = getProductEcommerceStock(product)
  for (const field of ORDER_COMBINED_WAREHOUSE_FIELDS) {
    total += parseProductStockField(product[field])
  }
  return total
}

function computeLegacyCatalogStock(
  product: Record<string, unknown>,
  options?: ComputeProductDisplayStockOptions
): number {
  let total = getProductBaseSaldo(product)
  for (const field of LEGACY_SUM_WAREHOUSE_FIELDS) {
    total += parseProductStockField(product[field])
  }
  if (options?.includeEcommerce) {
    total += getProductEcommerceStock(product)
  }
  return total
}

/** Recoge en bodega activo y bodega tipo Xerox (95 Xerox, 93 ML fullfillment, 94 ML Distrito Cafetero). */
export function isRecogeEnBodegaXerox(
  recogeEnBodega: boolean,
  bodegaRecogidaSeleccion: string
): boolean {
  return (
    recogeEnBodega &&
    (bodegaRecogidaSeleccion === '95' ||
      bodegaRecogidaSeleccion === '93' ||
      bodegaRecogidaSeleccion === '94')
  )
}

/** Recoge en bodega activo y bodega Ecommerce. */
export function isRecogeEnBodegaEcommerce(
  recogeEnBodega: boolean,
  bodegaRecogidaSeleccion: string
): boolean {
  return recogeEnBodega && bodegaRecogidaSeleccion === 'ecommerce'
}

/** Recoge en bodega activo y En tránsito (97; misma dirección/stock que Ecommerce). */
export function isRecogeEnBodegaEnTransito(
  recogeEnBodega: boolean,
  bodegaRecogidaSeleccion: string
): boolean {
  return recogeEnBodega && (bodegaRecogidaSeleccion === '97' || bodegaRecogidaSeleccion === '96')
}

export function getOrderBodegaStockMode(
  recogeEnBodega: boolean,
  bodegaRecogidaSeleccion: string
): OrderBodegaStockMode {
  if (isRecogeEnBodegaXerox(recogeEnBodega, bodegaRecogidaSeleccion)) {
    return 'xerox_only'
  }
  return 'combined'
}

export function getOrderStockDisplayOptions(
  recogeEnBodega: boolean,
  bodegaRecogidaSeleccion: string
): ComputeProductDisplayStockOptions {
  return {
    bodegaStockMode: getOrderBodegaStockMode(recogeEnBodega, bodegaRecogidaSeleccion)
  }
}

/** Cotizaciones y pantallas sin recogida en bodega: suma combined (sin Xerox ni Distrito). */
export function getCombinedStockDisplayOptions(): ComputeProductDisplayStockOptions {
  return { bodegaStockMode: 'combined' }
}

/** Etiqueta legible de la bodega para mensajes de validación de stock en pedidos. */
export function getRecogidaBodegaDisplayLabel(
  recogeEnBodega: boolean,
  bodegaRecogidaSeleccion: string
): string {
  if (!recogeEnBodega || !bodegaRecogidaSeleccion) {
    return 'General'
  }
  const fromCatalog = bodegaFacturacion.find(
    (b: { value: string; label: string }) => b.value === bodegaRecogidaSeleccion
  )
  if (fromCatalog?.label) {
    return fromCatalog.label
  }
  return 'General'
}

function computeStockByWarehouseMode(
  product: Record<string, unknown>,
  options?: ComputeProductDisplayStockOptions
): number {
  const mode = resolveBodegaStockMode(options)
  if (mode === 'xerox_only') {
    return getXeroxDisplayStock(product)
  }
  if (mode === 'ecommerce_only') {
    return getFulfillmentDisplayStock(product)
  }
  if (mode === 'combined') {
    return getFulfillmentDisplayStock(product)
  }
  return computeLegacyCatalogStock(product, options)
}

/**
 * Stock para mostrar/validar según modo de bodega de recogida.
 */
export function computeProductDisplayStock(
  product: Record<string, unknown> | null | undefined,
  options?: ComputeProductDisplayStockOptions
): number {
  if (!product) return 0

  const mode = resolveBodegaStockMode(options)

  if (mode === 'xerox_only') {
    const hasFlexyXerox = getFlexyWarehouseEntries(product).some(isFlexyXeroxWarehouse)
    const byMode = computeStockByWarehouseMode(product, options)
    if (hasFlexyXerox || isStockFieldPresent(product.stock_xerox)) {
      return byMode
    }
    if (isStockFieldPresent(product.stock_disponible)) {
      return getStockDisponibleSaldo(product)
    }
    return byMode
  }

  if (mode === 'combined' || mode === 'ecommerce_only') {
    const hasFlexyFulfillment = getFlexyWarehouseEntries(product).some(isFlexyFulfillmentWarehouse)
    const byMode = computeStockByWarehouseMode(product, options)
    if (hasFlexyFulfillment) {
      return byMode
    }
    if (hasPerWarehouseStockFields(product)) {
      return byMode
    }
    if (isStockFieldPresent(product.stock_disponible)) {
      return getStockDisponibleSaldo(product)
    }
    return byMode
  }

  if (!hasPerWarehouseStockFields(product)) {
    if (isStockFieldPresent(product.stock_disponible)) {
      return getStockDisponibleSaldo(product)
    }
    return computeStockByWarehouseMode(product, options)
  }

  const byMode = computeStockByWarehouseMode(product, options)
  if (byMode === 0 && isStockFieldPresent(product.stock_disponible)) {
    return getStockDisponibleSaldo(product)
  }
  return byMode
}

/** Recalcula stock a mostrar desde campos crudos del producto. */
export function getProductDetailDisplayStock(
  detail: Record<string, unknown> | null | undefined,
  options?: ComputeProductDisplayStockOptions
): number {
  if (!detail) return 0
  return computeProductDisplayStock(detail, options)
}

/**
 * Normaliza respuesta de GET /products o /products/multiple:
 * deja `stock` y `stock_quantity` con el total y conserva campos por bodega.
 */
export function enrichProductWithDisplayStock<T extends Record<string, unknown>>(
  product: T,
  options?: ComputeProductDisplayStockOptions
): T & {
  stock: number
  stock_quantity: number
  display_stock: number
  stock_xerox: number
  stock_gsm: number
  stock_distrito: number
  stock_bodtemporal: number
  stock_ecomerce: number
} {
  const display = computeProductDisplayStock(product, options)
  return {
    ...product,
    stock: display,
    stock_quantity: display,
    display_stock: display,
    stock_xerox: parseProductStockField(product.stock_xerox),
    stock_gsm: parseProductStockField(product.stock_gsm),
    stock_distrito: parseProductStockField(product.stock_distrito),
    stock_bodtemporal: parseProductStockField(product.stock_bodtemporal),
    stock_ecomerce: getProductEcommerceStock(product)
  }
}

/** Recalcula stock mostrado en productDetails (p. ej. al cambiar recoge en bodega). */
export function reapplyOrderProductDetailsStock(
  details: Record<string, Record<string, unknown>>,
  lineItems: Array<{ sku?: string; product_id?: string; is_combo?: number; components?: Array<{ sku: string }> }> | undefined,
  options: ComputeProductDisplayStockOptions
): Record<string, Record<string, unknown>> {
  if (!details || Object.keys(details).length === 0) return details
  const next: Record<string, Record<string, unknown>> = {}
  for (const [key, raw] of Object.entries(details)) {
    const display = computeProductDisplayStock(raw, options)
    next[key] = { ...raw, stock: display, stock_quantity: display, display_stock: display }
  }
  lineItems?.forEach(item => {
    if (item.is_combo !== 1 || !item.components?.length) return
    const stocks: number[] = []
    item.components.forEach(component => {
      const comp = next[component.sku]
      stocks.push(comp ? computeProductDisplayStock(comp, options) : 0)
    })
    if (stocks.length === 0) return
    const minStock = Math.min(...stocks)
    const comboKey = item.sku || item.product_id
    if (comboKey && next[comboKey]) {
      next[comboKey] = {
        ...next[comboKey],
        stock: minStock,
        stock_quantity: minStock,
        display_stock: minStock
      }
    }
  })
  return next
}

export type WarehouseStockAlternative = {
  label: string
  stock: number
}

const FLEXY_WAREHOUSE_SHORT_LABELS: Record<string, string> = {
  XEROX: 'Xerox',
  'EXHIBICIÓN XEROX': 'exhibicion xerox',
  ALMACENAMIENTO: 'almacen',
  FULFILLMENT: 'fulfillment',
  RESERVAS: 'reservas',
  SALDOS: 'saldos',
  DEVOLUCIONES: 'devoluciones',
  ECOMMERCE: 'ecommerce'
}

const OFIMA_WAREHOUSE_STOCK_FIELDS: Array<{ field: string; label: string }> = [
  { field: 'stock_xerox', label: 'Xerox' },
  { field: 'stock_ecomerce', label: 'Ecommerce' },
  { field: 'stock_ecommerce', label: 'Ecommerce' },
  { field: 'stock_gsm', label: 'GSM' },
  { field: 'stock_bodtemporal', label: 'Temporal' },
  { field: 'stock_distrito', label: 'Distrito' }
]

function formatFlexyWarehouseShortLabel(entry: FlexyWarehouseStockEntry): string {
  const nombre = String(entry.nombre ?? '').trim()
  const upper = nombre.toUpperCase()
  if (FLEXY_WAREHOUSE_SHORT_LABELS[upper]) {
    return FLEXY_WAREHOUSE_SHORT_LABELS[upper]
  }
  const codigo = String(entry.codigo ?? '').trim()
  if (codigo) {
    const codeBase = codigo.split('-')[0]?.toLowerCase()
    if (codeBase) return codeBase
  }
  if (nombre) {
    return nombre.split(/\s+/)[0].toLowerCase()
  }
  return 'bodega'
}

function getOfimaWarehouseAlternatives(
  product: Record<string, unknown>,
  quantityNeeded: number
): WarehouseStockAlternative[] {
  const alternatives: WarehouseStockAlternative[] = []
  const seenLabels = new Set<string>()
  for (const { field, label } of OFIMA_WAREHOUSE_STOCK_FIELDS) {
    if (!isStockFieldPresent(product[field]) || seenLabels.has(label)) continue
    const stock = parseProductStockField(product[field])
    if (stock > 0 && stock >= quantityNeeded) {
      seenLabels.add(label)
      alternatives.push({ label, stock })
    }
  }
  return alternatives
}

/** Bodegas con saldo positivo que cubren la cantidad solicitada (mensaje de ayuda en pedidos). */
export function getSufficientAlternativeWarehouseStocks(
  product: Record<string, unknown> | null | undefined,
  quantityNeeded: number
): WarehouseStockAlternative[] {
  if (!product || quantityNeeded <= 0) return []

  const flexyEntries = getFlexyWarehouseEntries(product)
  if (flexyEntries.length > 0) {
    return flexyEntries
      .map((entry) => {
        const stock = parseFlexyWarehouseEntryStock(entry)
        return stock == null
          ? null
          : { label: formatFlexyWarehouseShortLabel(entry), stock }
      })
      .filter(
        (entry): entry is WarehouseStockAlternative =>
          entry != null && entry.stock > 0 && entry.stock >= quantityNeeded
      )
  }

  return getOfimaWarehouseAlternatives(product, quantityNeeded)
}

export function formatWarehouseAlternativesText(
  alternatives: WarehouseStockAlternative[]
): string {
  if (alternatives.length === 0) return ''
  return alternatives
    .map((entry) => `${entry.label.toUpperCase()}=${entry.stock}`)
    .join(', ')
}

/** Mapeo estándar tras GET /products o /products/multiple (pedidos, cotizaciones, etc.). */
export function mapApiProductToOrderDetails(
  product: Record<string, unknown>,
  options?: ComputeProductDisplayStockOptions
) {
  const enriched = enrichProductWithDisplayStock(product, options)
  const sku = String(enriched.sku ?? '').trim()
  return {
    ...enriched,
    sku,
    precio_ofima: product.price,
    iva_ofima: product.tax_rate,
    price: product.price,
    tax_rate: product.tax_rate ?? 0.19
  }
}
